import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDashboardUrlState, type UseDashboardUrlStateResult } from '../../filter/useDashboardUrlState'
import { toDashboardFilter } from '../../filter/dashboardFilterReducer'
import type { WidgetProps } from '../../widgets/types'
import type { DashboardFilter } from '../../../../types/insights'

export interface UsePortfolioAnalyticsResult extends UseDashboardUrlStateResult {
  filter: DashboardFilter
  widgetProps: (widgetKey: string) => WidgetProps
  filterFieldsOpen: boolean
  setFilterFieldsOpen: (open: boolean) => void
}

/**
 * IB-282 View 1 — thin composition hook, mirrors `useJurisdictionAnalytics.ts`'s own shape exactly
 * (itself mirroring `DashboardScreen`'s top-of-component logic). Reuses `useDashboardUrlState`/
 * `toDashboardFilter` VERBATIM — both are already route-agnostic, not View-specific.
 */
export function usePortfolioAnalytics(): UsePortfolioAnalyticsResult {
  const navigate = useNavigate()
  const urlState = useDashboardUrlState()
  const [filterFieldsOpen, setFilterFieldsOpen] = useState(false)

  const filter: DashboardFilter = toDashboardFilter(urlState.state)

  function onDrill(rsql: string): void {
    navigate(`/cases/filings${rsql ? `?filter=${encodeURIComponent(rsql)}` : ''}`)
  }

  function widgetProps(widgetKey: string): WidgetProps {
    return {
      filter,
      skipRefetch: urlState.state.crossFilterOrigin === widgetKey,
      onCrossFilter: (axis, value) =>
        urlState.dispatch({ type: 'ADD_CROSSFILTER', axis, value, sourceWidget: widgetKey }),
      onDrill,
    }
  }

  return { ...urlState, filter, widgetProps, filterFieldsOpen, setFilterFieldsOpen }
}
